import { Transform } from 'stream';
import { doKeyInc } from '../utils/map';

const fromEudora = /(From \?\?\?@\?\?\?[^\r\n]*(?:\r\n|\r|\n))/g;
const fromGmail = /(From \d+@xxx[^\r\n]*(?:\r\n|\r|\n))/g;
const basicFrom = /((?:^|\r\n|\r|\n)From )/g;

export class SplitMbox extends Transform {
	public numEmittedMessages = 0;
	private _from = /((?:^|\r\n|\r|\n)From [^\r\n]*(?:\r\n|\r|\n))/g;
	private _doUnescape = false;

	// Rather than tracking the current chunk offset, we will track the offset of the current message
	private _streamPos = 0;
	private _startOfFile = '';
	private _currentMessage = '';
	private _streamMessageOffsets = false;

	public firstLine: string | undefined;
	public numExtraNonEscapedFroms = 0;
	public numRestoredFroms = 0;
	public numEscapedFroms = new Map<string, number>();
	public messageOffsets: MessageOffset[] = [];

	// Optional streamMessageOffsets argument sets a flag which will alter the stream output
	// from a sequence array of strings to a sequence of messages with messageOffsets included
	constructor(streamMessageOffsets?: boolean) {
		super({
			// Don't encode any incoming string chunks... assume they are already 'binary'
			decodeStrings: false,

			// This affects the "encoding" argument to _transform
			defaultEncoding: 'binary',

			writableObjectMode: true,

			readableObjectMode: true,
			readableHighWaterMark: 8,
		} as any);
		if (streamMessageOffsets) {
			this._streamMessageOffsets = streamMessageOffsets;
		}
	} // constructor()

	_transform(
		chunk: string | Buffer,
		encoding: string | undefined,
		cb: (err?: any) => void
	): void {
		if (typeof chunk !== 'string' && !Buffer.isBuffer(chunk)) {
			cb(new Error('Expected chunk to be a string or Buffer'));
			return;
		}
		if (this._streamPos === 0 && chunk.length === 0) {
			cb(new Error('Not an mbox: stream appears to be completely empty.'));
			return;
		}

		// Make sure we have something that looks like a string to work with
		let byteString: string;
		if (Buffer.isBuffer(chunk)) {
			byteString = chunk.toString('binary');
		} else {
			byteString = chunk;
		}

		if (!/[^\r\n](\r|\n|\r\n)/.test(this._startOfFile)) {
			// We do a few things differently at the start of the file, we don't really want
			// to do any splitting logic until we establish whether we have special headers
			// in play, like from Eudora or Gmail
			this._startOfFile += byteString;
			if (/[^\r\n](\r|\n|\r\n)/.test(this._startOfFile)) {
				// If newly finished startOfFile
				this.firstLine = this._startOfFile.split(/(\r|\n|\r\n)/)[0];
				if (fromEudora.test(this._startOfFile)) {
					this._from = fromEudora;
				} else if (fromGmail.test(this._startOfFile)) {
					this._from = fromGmail;
					this._doUnescape = true;
				}
			}
			this._currentMessage += byteString;
		} else {
			// else we are past the start of the file and are good to begin our usual checks
			this._currentMessage += byteString;
			if (this._from.test(this._currentMessage)) {
				// We track the messages and the things between the messages (From line, stored in odd indices)
				// so we can reconstruct a total offset for tracking purposes.
				const messagesAndDividers = this._currentMessage.split(this._from);
				// let dividers = this._currentMessage.match(this._from);
				// console.log("@@@" + this._currentMessage.replaceAll("\r", "R").replaceAll("\n", "N"));
				for (let mIx = 0; mIx < messagesAndDividers.length - 1; mIx += 2) {
					const dIx = mIx + 1;
					const res = this._trimAndNormalizeNewlines(
						messagesAndDividers[mIx],
						this._doUnescape
					);
					if (res.length > 0) {
						this.messageOffsets.push({
							start: this._streamPos,
							end: this._streamPos + messagesAndDividers[mIx].length,
						});
						++this.numEmittedMessages;
						this.numExtraNonEscapedFroms +=
							messagesAndDividers[mIx].split(basicFrom).filter((e, ix) => {
								return /[^\r\n]/.test(e);
							}).length - 1;
						if (this._streamMessageOffsets) {
							this.push({
								start: this._streamPos,
								end: this._streamPos + messagesAndDividers[mIx].length,
								message: res,
							});
						} else {
							this.push(res);
						}
					}
					this._streamPos +=
						messagesAndDividers[mIx].length + messagesAndDividers[dIx].length;
					// console.log("~~~" + messages[mIx].replaceAll("\r", "R").replaceAll("\n", "N") + "~~~" + messages[mIx].length);
					// console.log("===" + messagesAndDividers[dIx].replaceAll("\r", "R").replaceAll("\n", "N") + "===" + messagesAndDividers[dIx].length);
				}
				// Whatever's after the last _from delimiter, there most likely is more coming
				// after the chunk break, so we will store it in buffer and wait for more
				this._currentMessage = messagesAndDividers[messagesAndDividers.length - 1];
			}
		}

		cb();
	} // _transform()

	_flush(cb: (err?: any) => void): void {
		if (this._startOfFile.length < 5) {
			cb(new Error('Not an mbox: stream is too short to be an mbox'));
			return;
		}
		if (!this._startOfFile.startsWith('From ')) {
			cb(new Error('Not an mbox: expected valid mbox to start with "From "'));
			return;
		}

		// Whatever's left in _currentMessage, there's no more chunks coming, so we can just handle it.
		const messagesAndDividers = this._currentMessage.split(this._from);
		// let messagesAndDividers = this._currentMessage.match(this._from);
		for (let mIx = 0; mIx < messagesAndDividers.length; mIx += 2) {
			const dIx = mIx + 1;
			const res = this._trimAndNormalizeNewlines(messagesAndDividers[mIx], this._doUnescape);
			if (res.length > 0) {
				this.messageOffsets.push({
					start: this._streamPos,
					end: this._streamPos + messagesAndDividers[mIx].length,
				});
				++this.numEmittedMessages;
				this.numExtraNonEscapedFroms +=
					messagesAndDividers[mIx].split(basicFrom).filter((e, ix) => {
						return /[^\r\n]/.test(e);
					}).length - 1;
				if (this._streamMessageOffsets) {
					this.push({
						start: this._streamPos,
						end: this._streamPos + messagesAndDividers[mIx].length,
						message: res,
					});
				} else {
					this.push(res);
				}
			}

			// console.log("~~~~" + messages[i].replaceAll("\r", "R").replaceAll("\n", "N") + "~~~~" + messages[i].length);

			// Here, unlike in _transform, messagesAndDividers[dIx] may not exist, in case the flush buffer
			// is only a single message.
			if (messagesAndDividers[dIx]) {
				this._streamPos +=
					messagesAndDividers[mIx].length + messagesAndDividers[dIx].length;
			}
			// console.log("====" + dividers[i].replaceAll("\r", "R").replaceAll("\n", "N") + "====" + dividers[i].length);
		}

		// console.log(JSON.stringify(this.messageOffsets));
		cb();
	} // _flush()

	_trimAndNormalizeNewlines(arg: string, doUnescape: boolean): string {
		let res = arg;
		// Remove an \n, an \r, or possibly an \r\n which is superfluous at the end of the line,
		// but preserve additional trailing whitespace.
		if (res.endsWith('\n')) {
			res = res.substring(0, res.length - 1);
		}
		if (res.endsWith('\r')) {
			res = res.substring(0, res.length - 1);
		}
		res = res.replaceAll(/(?<!\r)\n|\r(?!\n)/g, '\r\n');
		if (doUnescape) {
			// We use group capturing here to slice off a '>' character from lines that begin with some number
			// of '>' characters followed by "From "
			res = res.replaceAll(/(\r\n>+)(From )/g, (match, p1, p2) => {
				doKeyInc(this.numEscapedFroms, p1);
				++this.numRestoredFroms;
				const unescapedGroup = p1.slice(0, -1);
				return unescapedGroup + p2;
			});
		}
		return res;
	} // _trimAndNormalizeNewlines()
} // class SplitMbox

export interface MessageOffset {
	start: number;
	end: number;
}
